import { useTranslation } from "@/hooks/useTranslation";
import { SettingsPasswordCard } from "@/pages/settings/components";
import { useGetStSetting, useUpdateStSetting } from "@/services";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Button,
	Card,
	CardContent,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Icon,
	Input,
	Switch,
	Typography,
} from "@st-marketplace/ui";
import { useEffect, useMemo } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";

type ThresholdRow = {
	threshold: string;
	percentage: number | "";
};

type STSettingsFormData = {
	seller_can_view_order_plan: boolean;
	version: string;
	system_usdt_trc_wallet: string;
	max_snipe_per_order: number | "";
	max_transfers_for_quick_sell: number | "";
	to_list_in_quicksell: number | "";
	default_commission_rate: number | "";
	snipe_failure_percent: number | "";
	one_shot_success_ratio_bonus: number | "";
	snipe_search_wait_enabled: boolean;
	snipe_search_wait_ms: number | "";
	transfer_p_sell_percentage_dict: ThresholdRow[];
	transfer_p_snipe_percentage_dict: ThresholdRow[];
};

const parseThreshold = (value: string) => Number(value.replace(/[,_\s]/g, ""));

const toThresholdRows = (
	dict: Record<string, number> | undefined,
): ThresholdRow[] => {
	if (!dict) return [{ threshold: "", percentage: "" }];

	const rows = Object.entries(dict)
		.map(([threshold, percentage]) => ({
			threshold,
			percentage,
		}))
		.sort(
			(a, b) =>
				parseThreshold(a.threshold || "0") - parseThreshold(b.threshold || "0"),
		);

	return rows.length > 0 ? rows : [{ threshold: "", percentage: "" }];
};

const toThresholdDict = (rows: ThresholdRow[]) =>
	rows.reduce<Record<string, number>>((acc, row) => {
		const parsedThreshold = parseThreshold(row.threshold);
		if (!Number.isFinite(parsedThreshold) || row.percentage === "") return acc;

		acc[String(Math.trunc(parsedThreshold))] = Number(row.percentage);
		return acc;
	}, {});

const settingsSchema = z.object({
	seller_can_view_order_plan: z.boolean(),
	version: z.string().min(1),
	system_usdt_trc_wallet: z.string().min(1),
	max_snipe_per_order: z.coerce.number().min(0),
	max_transfers_for_quick_sell: z.coerce.number().min(0),
	to_list_in_quicksell: z.coerce.number().min(0),
	default_commission_rate: z.coerce.number().min(0),
	snipe_failure_percent: z.coerce.number().min(0).max(100),
	one_shot_success_ratio_bonus: z.coerce.number().int().min(0).max(100),
	snipe_search_wait_enabled: z.boolean(),
	// only read when enabled; null is sent to the backend otherwise
	snipe_search_wait_ms: z.coerce.number().int().min(0).max(10_000),
	transfer_p_sell_percentage_dict: z
		.array(
			z.object({
				threshold: z.string().min(1),
				percentage: z.coerce.number().min(0).max(100),
			}),
		)
		.min(1),
	transfer_p_snipe_percentage_dict: z
		.array(
			z.object({
				threshold: z.string().min(1),
				percentage: z.coerce.number().min(0).max(100),
			}),
		)
		.min(1),
});

const STAdminSettings = () => {
	const { t } = useTranslation();
	const updateMutation = useUpdateStSetting();
	const { data: stSetting, isLoading } = useGetStSetting();

	const defaultValues = useMemo<STSettingsFormData>(
		() => ({
			seller_can_view_order_plan: false,
			version: "",
			system_usdt_trc_wallet: "",
			max_snipe_per_order: 3,
			max_transfers_for_quick_sell: 250,
			to_list_in_quicksell: 3500,
			default_commission_rate: 10,
			snipe_failure_percent: 80,
			one_shot_success_ratio_bonus: 10,
			snipe_search_wait_enabled: true,
			snipe_search_wait_ms: 60,
			transfer_p_sell_percentage_dict: [{ threshold: "", percentage: "" }],
			transfer_p_snipe_percentage_dict: [{ threshold: "", percentage: "" }],
		}),
		[],
	);

	const form = useForm<STSettingsFormData>({
		resolver: zodResolver(settingsSchema),
		defaultValues,
	});

	const snipeSearchWaitEnabled = form.watch("snipe_search_wait_enabled");

	const sellThresholds = useFieldArray({
		control: form.control,
		name: "transfer_p_sell_percentage_dict",
	});

	const snipeThresholds = useFieldArray({
		control: form.control,
		name: "transfer_p_snipe_percentage_dict",
	});

	useEffect(() => {
		if (!stSetting) return;

		form.reset(
			{
				seller_can_view_order_plan:
					stSetting.seller_can_view_order_plan ?? false,
				version: stSetting.version,
				system_usdt_trc_wallet: stSetting.system_usdt_trc_wallet,
				max_snipe_per_order: stSetting.max_snipe_per_order ?? 3,
				max_transfers_for_quick_sell:
					stSetting.max_transfers_for_quick_sell ?? 250,
				to_list_in_quicksell: stSetting.to_list_in_quicksell ?? 3500,
				default_commission_rate: Number(
					stSetting.default_commission_rate ?? "10",
				),
				snipe_failure_percent: stSetting.snipe_failure_percent ?? 80,
				one_shot_success_ratio_bonus:
					stSetting.one_shot_success_ratio_bonus ?? 10,
				snipe_search_wait_enabled: stSetting.snipe_search_wait_ms !== null,
				snipe_search_wait_ms: stSetting.snipe_search_wait_ms ?? 60,
				transfer_p_sell_percentage_dict: toThresholdRows(
					stSetting.transfer_p_sell_percentage_dict,
				),
				transfer_p_snipe_percentage_dict: toThresholdRows(
					stSetting.transfer_p_snipe_percentage_dict,
				),
			},
			{
				keepDirtyValues: true,
				keepErrors: true,
			},
		);
	}, [form, stSetting]);

	const onSubmit = form.handleSubmit((values) => {
		updateMutation.mutate({
			body: {
				key: "STSetting",
				seller_can_view_order_plan: values.seller_can_view_order_plan,
				version: values.version,
				system_usdt_trc_wallet: values.system_usdt_trc_wallet,
				max_snipe_per_order: Number(values.max_snipe_per_order),
				max_transfers_for_quick_sell: Number(
					values.max_transfers_for_quick_sell,
				),
				to_list_in_quicksell: Number(values.to_list_in_quicksell),
				default_commission_rate: Number(values.default_commission_rate),
				snipe_failure_percent: Number(values.snipe_failure_percent),
				one_shot_success_ratio_bonus: Number(
					values.one_shot_success_ratio_bonus,
				),
				// null disables it: the bid always waits for the search
				snipe_search_wait_ms: values.snipe_search_wait_enabled
					? Number(values.snipe_search_wait_ms)
					: null,
				transfer_p_sell_percentage_dict: toThresholdDict(
					values.transfer_p_sell_percentage_dict,
				),
				transfer_p_snipe_percentage_dict: toThresholdDict(
					values.transfer_p_snipe_percentage_dict,
				),
			},
		});
	});

	return (
		<div className="flex flex-col gap-2">
			<Typography variant="title-sm" as="h2">
				{t("admin.stSettings.title")}
			</Typography>
			<Typography variant="body-sm" className="mb-5 text-secondary-text-100">
				{t("admin.stSettings.description")}
			</Typography>

			<Card className="border-secondary-border mb-10">
				<CardContent className="px-[7%] py-10">
					<Form {...form}>
						<form noValidate onSubmit={onSubmit} className="w-full">
							<section className="mb-5 border-b border-secondary-border pb-10">
								<Typography variant="title-sm" as="h2" className="mb-5">
									{t("admin.stSettings.general")}
								</Typography>
								<FormField
									control={form.control}
									name="seller_can_view_order_plan"
									render={({ field }) => (
										<FormItem className="mb-6">
											<FormLabel>{t("orderPlan.settingLabel")}</FormLabel>
											<FormControl>
												<Switch
													checked={field.value}
													onCheckedChange={field.onChange}
													disabled={isLoading}
													aria-label={t("orderPlan.settingLabel")}
												/>
											</FormControl>
											<p className="text-sm text-secondary-text-100">
												{t("orderPlan.settingDescription")}
											</p>
										</FormItem>
									)}
								/>
								<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
									<FormField
										control={form.control}
										name="version"
										render={({ field }) => (
											<FormItem>
												<FormLabel required>
													{t("admin.stSettings.fields.version")}
												</FormLabel>
												<FormControl>
													<Input
														{...field}
														ltr
														placeholder={t("admin.stSettings.fields.version")}
														disabled={isLoading}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
									<FormField
										control={form.control}
										name="system_usdt_trc_wallet"
										render={({ field }) => (
											<FormItem>
												<FormLabel required>
													{t("admin.stSettings.fields.wallet")}
												</FormLabel>
												<FormControl>
													<Input
														{...field}
														ltr
														placeholder={t("admin.stSettings.fields.wallet")}
														disabled={isLoading}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
							</section>

							<section className="mb-5 border-b border-secondary-border pb-10">
								<Typography variant="title-sm" as="h2" className="mb-5">
									{t("admin.stSettings.transferRules")}
								</Typography>
								<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
									<FormField
										control={form.control}
										name="max_snipe_per_order"
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													{t("admin.stSettings.fields.maxSnipePerOrder")}
												</FormLabel>
												<FormControl>
													<Input
														type="number"
														placeholder={t(
															"admin.stSettings.fields.maxSnipePerOrder",
														)}
														value={field.value}
														onChange={field.onChange}
														disabled={isLoading}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
									<FormField
										control={form.control}
										name="max_transfers_for_quick_sell"
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													{t(
														"admin.stSettings.fields.maxTransfersForQuickSell",
													)}
												</FormLabel>
												<FormControl>
													<Input
														type="number"
														placeholder={t(
															"admin.stSettings.fields.maxTransfersForQuickSell",
														)}
														value={field.value}
														onChange={field.onChange}
														disabled={isLoading}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
									<FormField
										control={form.control}
										name="to_list_in_quicksell"
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													{t("admin.stSettings.fields.toListInQuicksell")}
												</FormLabel>
												<FormControl>
													<Input
														type="number"
														placeholder={t(
															"admin.stSettings.fields.toListInQuicksell",
														)}
														value={field.value}
														onChange={field.onChange}
														disabled={isLoading}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
									<FormField
										control={form.control}
										name="snipe_failure_percent"
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													{t("admin.stSettings.fields.snipeFailurePercent")}
												</FormLabel>
												<FormControl>
													<Input
														type="number"
														placeholder={t(
															"admin.stSettings.fields.snipeFailurePercent",
														)}
														value={field.value}
														onChange={field.onChange}
														disabled={isLoading}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
									<FormField
										control={form.control}
										name="one_shot_success_ratio_bonus"
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													{t(
														"admin.stSettings.fields.oneShotSuccessRatioBonus",
													)}
												</FormLabel>
												<FormControl>
													<Input
														type="number"
														min={0}
														max={100}
														value={field.value}
														onChange={field.onChange}
														disabled={isLoading}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
									<FormField
										control={form.control}
										name="snipe_search_wait_enabled"
										render={({ field }) => (
											<FormItem className="md:col-span-2">
												<FormLabel>
													{t("admin.stSettings.fields.snipeSearchWait")}
												</FormLabel>
												<div className="flex items-center gap-3">
													<FormControl>
														<Switch
															checked={field.value}
															onCheckedChange={field.onChange}
															disabled={isLoading}
														/>
													</FormControl>
													<Typography
														variant="body-sm"
														className="text-secondary-text-100"
													>
														{field.value
															? t("admin.stSettings.fields.snipeSearchWaitOn")
															: t("admin.stSettings.fields.snipeSearchWaitOff")}
													</Typography>
												</div>
											</FormItem>
										)}
									/>
									<FormField
										control={form.control}
										name="snipe_search_wait_ms"
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													{t("admin.stSettings.fields.snipeSearchWaitMs")}
												</FormLabel>
												<FormControl>
													<Input
														type="number"
														min={0}
														max={10_000}
														placeholder="60"
														value={field.value}
														onChange={field.onChange}
														disabled={isLoading || !snipeSearchWaitEnabled}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
							</section>

							<section className="mb-5 border-b border-secondary-border pb-10">
								<Typography variant="title-sm" as="h2" className="mb-5">
									{t("admin.stSettings.commission")}
								</Typography>
								<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
									<FormField
										control={form.control}
										name="default_commission_rate"
										render={({ field }) => (
											<FormItem>
												<FormLabel>
													{t("admin.stSettings.fields.defaultCommissionRate")}
												</FormLabel>
												<FormControl>
													<Input
														type="number"
														placeholder={t(
															"admin.stSettings.fields.defaultCommissionRate",
														)}
														value={field.value}
														onChange={field.onChange}
														disabled={isLoading}
													/>
												</FormControl>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>
							</section>

							<section className="mb-5 border-b border-secondary-border pb-10">
								<Typography variant="title-sm" as="h2" className="mb-5">
									{t("admin.stSettings.sellThresholds")}
								</Typography>
								<div className="flex flex-col gap-4">
									{sellThresholds.fields.map((field, index) => (
										<div
											key={field.id}
											className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-start"
										>
											<div className="w-full">
												<FormField
													control={form.control}
													name={
														`transfer_p_sell_percentage_dict.${index}.threshold` as const
													}
													render={({ field }) => (
														<FormItem>
															<FormLabel required>
																{t("admin.stSettings.threshold")}
															</FormLabel>
															<FormControl>
																<Input {...field} ltr placeholder="20_000" />
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
											<div className="w-full">
												<FormField
													control={form.control}
													name={
														`transfer_p_sell_percentage_dict.${index}.percentage` as const
													}
													render={({ field }) => (
														<FormItem>
															<FormLabel required>
																{t("admin.stSettings.percentage")}
															</FormLabel>
															<FormControl>
																<Input
																	type="number"
																	placeholder={t("admin.stSettings.percentage")}
																	value={field.value}
																	onChange={field.onChange}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
											<Button
												type="button"
												icon
												variant="text"
												color="ghost"
												size="sm"
												className="mb-1 shrink-0 self-end"
												aria-label={`${t("admin.stSettings.threshold")} ${
													index + 1
												}`}
												disabled={sellThresholds.fields.length === 1}
												onClick={() => sellThresholds.remove(index)}
											>
												<Icon name="delete-bin-line" size="sm" />
											</Button>
										</div>
									))}
									<div>
										<Button
											type="button"
											color="secondary"
											startIcon={<Icon name="add-line" />}
											onClick={() =>
												sellThresholds.append({ threshold: "", percentage: "" })
											}
										>
											{t("admin.stSettings.addThreshold")}
										</Button>
									</div>
								</div>
							</section>

							<section className="mb-5 border-b border-secondary-border pb-10">
								<Typography variant="title-sm" as="h2" className="mb-5">
									{t("admin.stSettings.snipeThresholds")}
								</Typography>
								<div className="flex flex-col gap-4">
									{snipeThresholds.fields.map((field, index) => (
										<div
											key={field.id}
											className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-center"
										>
											<div className="w-full">
												<FormField
													control={form.control}
													name={
														`transfer_p_snipe_percentage_dict.${index}.threshold` as const
													}
													render={({ field }) => (
														<FormItem>
															<FormLabel required>
																{t("admin.stSettings.threshold")}
															</FormLabel>
															<FormControl>
																<Input {...field} ltr placeholder="20_000" />
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
											<div className="w-full">
												<FormField
													control={form.control}
													name={
														`transfer_p_snipe_percentage_dict.${index}.percentage` as const
													}
													render={({ field }) => (
														<FormItem>
															<FormLabel required>
																{t("admin.stSettings.percentage")}
															</FormLabel>
															<FormControl>
																<Input
																	type="number"
																	placeholder={t("admin.stSettings.percentage")}
																	value={field.value}
																	onChange={field.onChange}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
											</div>
											<Button
												type="button"
												icon
												variant="text"
												color="ghost"
												size="sm"
												className="mb-1 shrink-0 self-end"
												aria-label={`${t("admin.stSettings.threshold")} ${
													index + 1
												}`}
												disabled={snipeThresholds.fields.length === 1}
												onClick={() => snipeThresholds.remove(index)}
											>
												<Icon name="delete-bin-line" size="sm" />
											</Button>
										</div>
									))}
									<div>
										<Button
											type="button"
											color="secondary"
											startIcon={<Icon name="add-line" />}
											onClick={() =>
												snipeThresholds.append({
													threshold: "",
													percentage: "",
												})
											}
										>
											{t("admin.stSettings.addThreshold")}
										</Button>
									</div>
								</div>
							</section>

							<div className="flex justify-end">
								<Button
									type="submit"
									color="primary"
									size="lg"
									disabled={isLoading || updateMutation.isPending}
									loading={updateMutation.isPending}
								>
									{t("button.submit")}
								</Button>
							</div>
						</form>
					</Form>
				</CardContent>
			</Card>

			<SettingsPasswordCard />
		</div>
	);
};

export default STAdminSettings;
